import moment from 'moment'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidBuildingIcon from 'LuxKit/Icons/solid/SolidBuildingIcon'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import CancellationPolicyDateChanges from './CancellationPolicyDateChanges'
import { TimelineNode, TimelineEndNode } from './CancellationPolicyTimelineComponents'
import {
  formatFlexibleCancellationPolicyDate,
  getFlexibleCancellationDateChangeAllowance,
} from './flexibleCancellationPolicyTimelineUtils'
import type { CashRefundCancellationTimelineDetails } from './cashRefundCancellationPolicyTimelineUtils'

interface Props {
  details: CashRefundCancellationTimelineDetails
}

/**
 * Timeline for the Fully refundable (cash refund) option: a full refund to the original payment
 * method until 10 days before check-in, then no refund. Shared by the offer-page option modal and
 * the checkout cancellation policy section.
 */
function CashRefundCancellationPolicyTimelineContent(props: Props) {
  const { details } = props
  const { today, checkInDate, deadline, days, numberOfDateChangesAllowed } = details
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const fullRefundColour = useRebrandSuccessDarkColour()

  const formatDateForPolicy = useCallback((date: string | moment.Moment) => formatFlexibleCancellationPolicyDate(
    date,
    today,
    intlDateFormatter,
  ), [intlDateFormatter, today])

  const dateChangeAllowance = useMemo(
    () => getFlexibleCancellationDateChangeAllowance(numberOfDateChangesAllowed),
    [numberOfDateChangesAllowed],
  )

  const fullRefundHeading = intl.formatMessage({
    id: 'IPvnv3wz6i5O1pItlNwB',
    defaultMessage: 'Full refund',
    description: 'Section heading for full refund in no-dates cancellation modal',
  })
  const noRefundHeading = intl.formatMessage({
    id: 'yAazKuRHWgNT7wkPi4c9',
    defaultMessage: 'No refund',
    description: 'Section heading for no-refund period in a modal dialog',
  })

  if (!checkInDate || !deadline) {
    return (
      <Group direction="vertical">
        <TimelineNode
          label={intl.formatMessage({
            id: '9e42YUlMptEMf3Fo2_tE',
            defaultMessage: 'More than {days} days before check-in',
            description: 'Timeline label for the credit refund period before check-in in cancellation modal without dates',
          }, { days })}
        >
          <BodyText variant="medium" weight="bold" colour={fullRefundColour}>{fullRefundHeading}</BodyText>
          <BodyText variant="medium" as="p">
            {intl.formatMessage({
              id: 'utCE-AOiavkepuKzs9pO',
              defaultMessage: 'Cancel more than {days} days before check-in to get a full refund to your original payment method.',
              description: 'Full refund body in the Fully refundable cancellation timeline without dates',
            }, { days })}
          </BodyText>
        </TimelineNode>
        <TimelineNode
          label={intl.formatMessage({
            id: 'tl7_BtwbsRRz0pGZvRke',
            defaultMessage: 'Fewer than {days} days before check-in',
            description: 'Timeline label for the no-refund period before check-in in cancellation modal without dates (non-charter)',
          }, { days })}
        >
          <BodyText variant="medium" weight="bold">{noRefundHeading}</BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '1Y2aN6fen5SPgReqFeDM',
              defaultMessage: 'Cancellations made fewer than {days} days before check-in are non-refundable.',
              description: 'No refund body for non-charter in the cancellation or refund flow',
            }, { days })}
          </BodyText>
        </TimelineNode>
        <TimelineEndNode
          icon={SolidBuildingIcon}
          label={intl.formatMessage({
            id: 'poCu5gwZHqlbN808pfIt',
            defaultMessage: 'Check-in',
            description: 'Label for check-in date on cancellation timeline without dates (non-charter)',
          })}
        />
      </Group>
    )
  }

  const noRefundDate = moment(deadline).add(1, 'day')
  const isDeadlineToday = deadline.isSame(today, 'day')
  const dateChangeWindow = {
    startDate: formatDateForPolicy(today),
    endDate: formatDateForPolicy(deadline),
  }

  return (
    <>
      <Group direction="vertical">
        <TimelineNode
          label={isDeadlineToday
            ? intl.formatMessage({
                id: '3WWQUkwCPrh4pVS0MjbK',
                defaultMessage: 'Up to {date} · Today',
                description: 'Date label showing end of full refund period when it is today in cancellation timeline',
              }, { date: formatDateForPolicy(deadline) })
            : intl.formatMessage({
                id: 'l3pEXYwwkXfvG_HOiItm',
                defaultMessage: 'Up to {date}',
                description: 'Date label showing the end of the full refund period in cancellation timeline',
              }, { date: formatDateForPolicy(deadline) })}
        >
          <BodyText variant="medium" weight="bold" colour={fullRefundColour}>{fullRefundHeading}</BodyText>
          <BodyText variant="medium" as="p">
            {intl.formatMessage({
              id: 'sGlr4aVP07pJM5YixngA',
              defaultMessage: 'Cancel before {date}, 11:59 pm to get a full refund of accommodation purchase price to your original payment method.',
              description: 'Full refund body in the Fully refundable cancellation timeline with dates',
            }, { date: formatDateForPolicy(deadline) })}
          </BodyText>
        </TimelineNode>
        <TimelineNode label={formatDateForPolicy(noRefundDate)}>
          <BodyText variant="medium" weight="bold">{noRefundHeading}</BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'ah8w_Ec4UjW_D49bKyOc',
              defaultMessage: 'Cancellations after {date}, 11:59 pm are non-refundable.',
              description: 'No refund body in with-dates cancellation modal in a modal dialog',
            }, { date: formatDateForPolicy(deadline) })}
          </BodyText>
        </TimelineNode>
        <TimelineEndNode
          icon={SolidBuildingIcon}
          label={intl.formatMessage({
            id: 'd59NymZ4PuZiqmggDhvS',
            defaultMessage: '{date} · Check-in',
            description: 'Check-in date with check-in label on cancellation timeline (non-charter)',
          }, { date: formatDateForPolicy(checkInDate) })}
        />
      </Group>

      <Hr size="md" />

      <CancellationPolicyDateChanges dateChangeAllowance={dateChangeAllowance} {...dateChangeWindow} />
    </>
  )
}

export default CashRefundCancellationPolicyTimelineContent
